import { User } from 'lucide-react'
import { Navigate } from 'react-router'

import accountantHero from '@/assets/marketing/images/accountant-hero.jpg'
import accountantLiveBookkeeper from '@/assets/marketing/images/accountant-live-bookkeeper.jpg'
import { Anchor } from '@/components/ui/Anchor'
import { Button } from '@/components/ui/button'
import { bookkeeperHref } from '@/config/config'
import { trackMarketingCta } from '@/lib/analytics'
import { useHostGuard } from '@/lib/hostGuard'
import { BookkeeperPricing } from './components/BookkeeperPricing'
import { BookkeeperProductVisual } from './components/BookkeeperProductVisual'
import { NavBar } from './components/NavBar'
import { Reveal } from './components/Reveal'
import { SiteFooter } from './components/SiteFooter'
import { StepCard } from './components/StepCard'
import { PRODUCT_NAV_ITEMS } from './content/productNavItems'
import {
  SITE_FOOTER_LEGAL_LINKS,
  SITE_FOOTER_LINK_COLUMNS,
  SITE_FOOTER_TAGLINE,
} from './content/siteFooterContent'

/* Standalone Bookkeeper landing page — own header/footer, no MarketingLayout, same pattern as FuelApp.tsx. */

const LOGIN_URL = bookkeeperHref('/')

/** bookkeeperHref, not the literal URL — staging and production sit on different TLDs. */
const SIGNUP_HREF = bookkeeperHref('/bookkeeper/start')

const CONTACT_HREF = 'mailto:bookkeeper@cai.ai'

const HOW_IT_WORKS = [
  {
    number: '01',
    title: 'Connect your accounts',
    body: 'Bank account, payment processor, factoring company. No fees to connect.',
  },
  {
    number: '02',
    title: 'Get real-time insights',
    body: 'See where your fleet is spending, and how you might start saving. Fuel, repairs, tolls, and payments are sorted as they come in.',
  },
  {
    number: '03',
    title: 'Know where you stand at month end',
    body: 'Cai closes the books for you within 5 days.',
  },
]

const BENEFITS = [
  {
    number: '0',
    title: 'Hours of data entry',
    body: 'Cai categorizes transactions for you automatically, and lets you know if it needs your review.',
  },
  {
    number: '0',
    title: 'Spreadsheets to keep up',
    body: 'Nothing to update on Sunday night and nothing to reconcile at tax time.',
  },
  {
    number: '5',
    title: 'Days to close the books',
    body: "Five days, not twenty or more, so you don't have to wait to find out where you landed each month.",
  },
]

export function Bookkeeper() {
  const { redirecting, navigateTo } = useHostGuard('marketing')
  if (navigateTo) return <Navigate to={navigateTo} replace />
  if (redirecting) return null

  return (
    <div className="bg-surface min-h-screen w-full shrink-0">
      <title>Cai | Bookkeeper</title>
      <meta
        name="description"
        content="While you run your fleet, Cai runs the books. Connect your accounts, then let Cai Bookkeeper keep the books current and show you where your money is going."
      />

      <NavBar
        theme="light"
        navLinks={[
          { label: 'About', href: '/#about' },
          { label: 'Products', items: PRODUCT_NAV_ITEMS, active: true },
          { label: 'Blog', href: 'https://blog.cai.ai', external: true },
        ]}
        utilityLinks={[
          {
            label: 'Log in',
            icon: <User />,
            href: LOGIN_URL,
            onClick: () => trackMarketingCta('bookkeeper_nav_login', { destination: LOGIN_URL }),
          },
        ]}
        ctaLabel="Create your account"
        ctaHref={SIGNUP_HREF}
        onCtaClick={() => trackMarketingCta('bookkeeper_nav_signup', { destination: SIGNUP_HREF })}
      />

      <section className="mx-auto grid max-w-(--maxw) grid-cols-2 items-center gap-14 px-5.5 py-24 max-lg:grid-cols-1">
        <Reveal>
          <span className="text-brand-800 font-mono text-xs font-bold tracking-[0.14em] uppercase">
            AI bookkeeping
          </span>
          <h1 className="h1-hero mt-5.5 max-w-[17ch] text-pretty">
            While you run your fleet, Cai runs the books
          </h1>
          <p className="text-ink-600 mt-6.5 max-w-130 text-xl leading-[1.6] text-pretty">
            Connect your accounts, then let your AI bookkeeper keep the books current and show you where your
            money is going.
          </p>
          <div className="mt-10 flex flex-wrap gap-3">
            <Button
              size="lg"
              href={SIGNUP_HREF}
              onClick={() => trackMarketingCta('bookkeeper_hero_signup', { destination: SIGNUP_HREF })}
            >
              Create your account
            </Button>
            <Button variant="outline" size="lg" href="#pricing">
              See pricing
            </Button>
          </div>
        </Reveal>
        <Reveal
          delay={150}
          className="relative min-h-[clamp(320px,34vw,460px)] w-full overflow-hidden rounded-sm"
        >
          {/* Positioned, not sized by percentage — see LargeFleets: h-full against a min-height parent breaks in Safari. */}
          <img
            src={accountantHero}
            alt="An owner-operator checking his phone next to his truck"
            className="absolute inset-0 h-full w-full object-cover"
          />
        </Reveal>
      </section>

      <section className="bg-ink-900 px-5.5 py-24 lg:py-32">
        <div className="mx-auto grid max-w-(--maxw) grid-cols-3 gap-4 max-lg:grid-cols-1">
          {HOW_IT_WORKS.map((step, i) => (
            <Reveal key={step.number} delay={i * 100}>
              <StepCard number={step.number} title={step.title}>
                {step.body}
              </StepCard>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-(--maxw) px-5.5 py-24 lg:py-32">
        <div className="grid grid-cols-2 items-center gap-14 max-lg:grid-cols-1">
          <div className="flex flex-col gap-8">
            <Reveal>
              <h2 className="h2 text-pretty">What you get</h2>
            </Reveal>
            <div className="flex flex-col">
              {BENEFITS.map((benefit, i) => (
                <Reveal key={benefit.title} delay={i * 80}>
                  <div className="border-ink-100 grid grid-cols-[72px_minmax(0,1fr)] gap-6 border-t py-7 max-md:grid-cols-1 max-md:gap-2">
                    <div className="text-brand-800 font-mono text-5xl font-bold">{benefit.number}</div>
                    <div className="flex flex-col gap-2">
                      <h3 className="text-2xl font-bold tracking-[-0.01em]">{benefit.title}</h3>
                      <p className="text-md text-ink-600 leading-[1.55] text-pretty">{benefit.body}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
          <Reveal delay={150}>
            <BookkeeperProductVisual />
          </Reveal>
        </div>
      </section>

      <section className="bg-ink-50 px-5.5 py-24 lg:py-32">
        <div className="mx-auto grid max-w-(--maxw) grid-cols-2 items-center gap-14 max-lg:grid-cols-1">
          <Reveal className="relative min-h-[clamp(340px,36vw,470px)] w-full overflow-hidden rounded-sm">
            <img
              src={accountantLiveBookkeeper}
              alt="A bookkeeper on the phone at a desk"
              className="absolute inset-0 h-full w-full object-cover"
            />
          </Reveal>
          <Reveal delay={150} className="flex flex-col gap-5">
            <h2 className="h2 text-pretty">Support from a live (human) bookkeeper</h2>
            <p className="text-md text-ink-600 max-w-120 leading-[1.55]">
              A bookkeeper will help you get set up and connect your accounts. After that, you meet with them
              quarterly to review your books. Included, not an add-on.
            </p>
          </Reveal>
        </div>
      </section>

      <BookkeeperPricing
        ctaHref={SIGNUP_HREF}
        onCtaClick={() => trackMarketingCta('bookkeeper_pricing_signup', { destination: SIGNUP_HREF })}
        onContactClick={() => trackMarketingCta('bookkeeper_pricing_contact')}
      />

      <section id="get-started" className="bg-ink-800 px-5.5 py-24 lg:py-32">
        <div className="mx-auto max-w-(--maxw)">
          <Reveal className="flex flex-col gap-6">
            <span className="text-brand-500 font-mono text-xs font-bold tracking-[0.14em] uppercase">
              Try free for 3 months
            </span>
            <h2 className="h2 max-w-[20ch] text-pretty text-white">
              See where your fleet&apos;s money is really going
            </h2>
            <p className="max-w-120 text-lg leading-[1.6] text-white/70">
              After you create your Cai Bookkeeper account, you&apos;ll be matched with a live bookkeeper to
              help you get set up.
            </p>
            <div className="mt-4 flex flex-wrap items-center gap-6">
              <Button
                size="lg"
                href={SIGNUP_HREF}
                onClick={() => trackMarketingCta('bookkeeper_footer_signup', { destination: SIGNUP_HREF })}
              >
                Create your account
              </Button>
              <Anchor href={CONTACT_HREF} className="text-md text-white/70 underline hover:text-white">
                Questions? Email us
              </Anchor>
            </div>
          </Reveal>
        </div>
      </section>

      <SiteFooter
        hideEdgeLines
        tagline={SITE_FOOTER_TAGLINE}
        linkColumns={SITE_FOOTER_LINK_COLUMNS}
        legalLinks={SITE_FOOTER_LEGAL_LINKS}
        note={
          <>
            <p>Cai Bookkeeper is not accounting, tax, or legal advice.</p>
            <p>
              If you are having difficulty viewing or navigating the content on this website, or find a
              feature or functionality that is not fully accessible to you, please email us at{' '}
              <a href="mailto:hello@cai.ai">hello@cai.ai</a>.
            </p>
          </>
        }
      />
    </div>
  )
}
